import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, UserCheck, Mail, Clock } from "lucide-react";

const ROLES = ["admin", "manager", "leader", "sr_engineer", "jr_engineer", "drafter", "qa1", "qa2", "final_qc", "hr_manager", "office_assistant"];

export default function PendingApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});  // { user_id: { role, team, team_role } }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pending-users");
      setPending(data || []);
    } catch {
      toast.error("Failed to load pending users");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setEdit = (uid, field, val) => {
    setEdits((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: val } }));
  };

  const approve = async (u) => {
    try {
      await api.post(`/admin/pending-users/${u.user_id}/approve`, edits[u.user_id] || {});
      toast.success(`Approved ${u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const reject = async (u) => {
    const reason = prompt(`Reject ${u.email}? Optional reason:`);
    if (reason === null) return;
    try {
      await api.post(`/admin/pending-users/${u.user_id}/reject`, { reason });
      toast.success(`Rejected ${u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">USER MANAGEMENT</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Pending Approvals</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            {pending.length} {pending.length === 1 ? "user" : "users"} awaiting your review.
          </p>
        </div>
        <button onClick={load} className="brut-btn-secondary px-3 py-2 text-xs font-mono" data-testid="pending-refresh-btn">REFRESH</button>
      </div>

      {loading && <div className="brut-card p-10 text-center font-mono text-sm text-zinc-500">LOADING…</div>}

      {!loading && pending.length === 0 && (
        <div className="brut-card p-10 text-center font-mono text-sm text-zinc-500" data-testid="pending-empty">
          <UserCheck className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
          No pending registrations. Approved users appear in <a href="/team" className="underline">Team</a>.
        </div>
      )}

      {!loading && pending.length > 0 && (
        <div className="brut-card overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-zinc-900 text-white">
                <th className="text-left px-3 py-2 label-mono text-white">User</th>
                <th className="text-left px-3 py-2 label-mono text-white">Source</th>
                <th className="text-left px-3 py-2 label-mono text-white">Registered</th>
                <th className="text-left px-3 py-2 label-mono text-white">Role at approval</th>
                <th className="text-left px-3 py-2 label-mono text-white">Team</th>
                <th className="text-left px-3 py-2 label-mono text-white">Team Role</th>
                <th className="text-right px-3 py-2 label-mono text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((u) => {
                const e = edits[u.user_id] || {};
                return (
                  <tr key={u.user_id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`pending-row-${u.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-yellow-200 border border-zinc-900 flex items-center justify-center font-mono text-xs font-bold">
                          {u.name && u.name[0] ? u.name[0].toUpperCase() : "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold truncate">{u.name || "—"}</div>
                          <div className="text-[11px] font-mono text-zinc-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-zinc-900 bg-zinc-100">
                        {u.registration_source === "google_oauth" ? "GOOGLE" : "EMAIL"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[11px] text-zinc-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {(u.registered_at || u.created_at || "").slice(0, 16).replace("T", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="brut-input px-2 py-1 text-xs font-mono"
                        value={e.role ?? u.role ?? "jr_engineer"}
                        onChange={(ev) => setEdit(u.user_id, "role", ev.target.value)}
                        data-testid={`pending-role-${u.user_id}`}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="brut-input px-2 py-1 text-xs w-28"
                        value={e.team ?? u.team ?? ""}
                        onChange={(ev) => setEdit(u.user_id, "team", ev.target.value)}
                        placeholder="Drafting-A"
                        data-testid={`pending-team-${u.user_id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="brut-input px-2 py-1 text-xs font-mono"
                        value={e.team_role ?? u.team_role ?? "employee"}
                        onChange={(ev) => setEdit(u.user_id, "team_role", ev.target.value)}
                        data-testid={`pending-team-role-${u.user_id}`}
                      >
                        {["admin", "manager", "lead", "employee"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => approve(u)}
                        className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 bg-green-300 hover:bg-green-400 inline-flex"
                        data-testid={`pending-approve-${u.user_id}`}
                      ><CheckCircle2 className="w-3.5 h-3.5" /> APPROVE</button>
                      <button
                        onClick={() => reject(u)}
                        className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 ml-2 inline-flex"
                        data-testid={`pending-reject-${u.user_id}`}
                      ><XCircle className="w-3.5 h-3.5" /> REJECT</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
